import { ref, computed } from 'vue';
import { useStore } from 'vuex';
import { useToast } from 'primevue/usetoast';

export function useShopDeliverySync() {
  const store = useStore();
  const toast = useToast();
  
  // État
  const isSyncing = ref(false);
  const hasLocalChanges = ref(false);
  const hasSyncErrors = ref(false);
  
  // Propriétés calculées à partir du store
  const isOnline = computed(() => store.getters['sync/isOnline']);
  const isUserOfflineMode = computed(() => store.getters.isUserOfflineMode);
  
  /**
   * Normaliser les statuts pour assurer la cohérence
   * @param {Object} updates - Les mises à jour à normaliser
   * @returns {Object} - Les mises à jour normalisées
   */
  const normalizeUpdates = (updates) => {
    const normalized = { ...updates };
    
    // Normaliser le status en minuscules si présent
    if (normalized.status) {
      normalized.status = normalized.status.toLowerCase();
    }
    
    return normalized;
  };
  
  /**
   * Mettre à jour l'état local d'une livraison shop
   * @param {Object} delivery - La livraison shop
   * @param {Object} updates - Les mises à jour à appliquer
   * @returns {Promise<Object>} - Résultat de l'opération
   */
  const updateShopDelivery = async (delivery, updates) => {
    try {
      if (!delivery || !delivery.id) {
        throw new Error('Données de livraison invalides');
      }
      
      // Normaliser les mises à jour pour assurer la cohérence
      const normalizedUpdates = normalizeUpdates(updates);
      
      // Créer une copie de la livraison avec les mises à jour
      const updatedDelivery = {
        ...delivery,
        ...normalizedUpdates
      };
      
      // Mettre à jour le store
      store.commit('UPDATE_CURRENT_DELIVERY', normalizedUpdates);
      
      // Si en ligne, essayer de synchroniser immédiatement
      if (isOnline.value && !isUserOfflineMode.value) {
        isSyncing.value = true;
        
        try {
          // Effectuer l'appel API pour mettre à jour le shop delivery
          const response = await store.dispatch('sync/updateShopDelivery', {
            id: delivery.id,
            updates: normalizedUpdates
          });
          
          // Mise à jour réussie
          hasLocalChanges.value = false;
          hasSyncErrors.value = false;
          
          toast.add({
            severity: 'success',
            summary: 'Synchronisation',
            detail: 'Mise à jour enregistrée avec succès',
            life: 3000
          });
          
          // Forcer le rafraîchissement dans MyTournee en émettant un événement spécifique
          emitShopDeliveryEvent(delivery.id, normalizedUpdates.status);
          
          return { success: true, synced: true, delivery: updatedDelivery };
        } catch (error) {
          console.error('Erreur de synchronisation:', error);
          
          // Enregistrer localement et marquer comme non synchronisé
          hasLocalChanges.value = true;
          
          // Ajouter à la file d'attente de synchronisation
          const { syncService } = await import('@/services/syncService');
          await syncService.queueUpdate('shop_delivery', delivery.id, normalizedUpdates);
          
          toast.add({
            severity: 'warn',
            summary: 'Synchronisation différée',
            detail: 'La modification sera synchronisée ultérieurement',
            life: 3000
          });
          
          // Même en cas d'erreur, émettre l'événement pour forcer le rafraîchissement
          emitShopDeliveryEvent(delivery.id, normalizedUpdates.status);
          
          return { success: true, synced: false, delivery: updatedDelivery };
        } finally {
          isSyncing.value = false;
        }
      } else {
        // En mode hors ligne, enregistrer localement
        hasLocalChanges.value = true;
        
        // Enregistrer dans une file d'attente de synchronisation
        const { syncService } = await import('@/services/syncService');
        await syncService.queueUpdate('shop_delivery', delivery.id, normalizedUpdates);
        
        toast.add({
          severity: 'info',
          summary: 'Mode hors ligne',
          detail: 'La modification sera synchronisée lors de la prochaine connexion',
          life: 3000
        });
        
        // Émettre l'événement même en mode hors ligne
        emitShopDeliveryEvent(delivery.id, normalizedUpdates.status);
        
        return { success: true, synced: false, delivery: updatedDelivery };
      }
    } catch (error) {
      console.error('Erreur lors de la mise à jour:', error);
      
      toast.add({
        severity: 'error',
        summary: 'Erreur',
        detail: 'Une erreur est survenue lors de la mise à jour',
        life: 3000
      });
      
      return { success: false, error: error.message || 'Erreur inconnue' };
    }
  };
  
  /**
   * Vérifier si une livraison shop a des modifications locales en attente
   * @param {string|number} deliveryId - ID de la livraison à vérifier
   * @returns {Promise<boolean>} - Vrai si des modifications sont en attente
   */
  const checkPendingUpdates = async (deliveryId) => {
    try {
      // Interroger le service de synchronisation
      const { syncService } = await import('@/services/syncService');
      const pendingData = await syncService.exportPendingData();
      
      // Filtrer pour trouver les mises à jour pour cette livraison
      const deliveryUpdates = pendingData.updates.filter(
        update => update.deliveryId === String(deliveryId) && update.type === 'shop_delivery'
      );
      
      // Mettre à jour l'état local
      hasLocalChanges.value = deliveryUpdates.length > 0;
      
      // Vérifier s'il y a des erreurs de synchro
      hasSyncErrors.value = deliveryUpdates.some(
        update => update.requiresManualSync || (update.syncAttempts && update.syncAttempts >= 3)
      );
      
      return hasLocalChanges.value;
    } catch (error) {
      console.error('Erreur lors de la vérification des mises à jour en attente:', error);
      return false;
    }
  };
  
  /**
   * Forcer la synchronisation des mises à jour en attente
   * @returns {Promise<Object>} - Résultat de l'opération
   */
  const syncPendingUpdates = async () => {
    if (!isOnline.value) {
      toast.add({
        severity: 'warn', 
        summary: 'Hors ligne',
        detail: 'La synchronisation n\'est pas disponible en mode hors ligne',
        life: 3000
      });
      return { success: false, reason: 'offline' };
    }
    
    try {
      isSyncing.value = true;
      
      // Appeler le service de synchronisation réel
      const { syncService } = await import('@/services/syncService');
      const result = await syncService.syncAll();
      
      // Mise à jour de l'état en fonction des résultats
      if (result.success > 0) {
        toast.add({
          severity: 'success',
          summary: 'Synchronisation',
          detail: `${result.success} modification(s) synchronisée(s) avec succès`,
          life: 3000
        });
      }
      
      if (result.failed > 0) {
        toast.add({
          severity: 'warn', 
          summary: 'Synchronisation partielle',
          detail: `${result.failed} modification(s) n'ont pas pu être synchronisées`,
          life: 4000
        });
      }
      
      // Vérifier à nouveau les mises à jour en attente
      await checkPendingUpdates(store.state.currentDelivery?.id);
      
      return result;
    } catch (error) {
      console.error('Erreur lors de la synchronisation forcée:', error);
      
      toast.add({
        severity: 'error',
        summary: 'Erreur',
        detail: 'La synchronisation a échoué: ' + (error.message || 'Erreur inconnue'),
        life: 3000
      });
      
      return { success: false, error: error.message };
    } finally {
      isSyncing.value = false;
    }
  };
  
  // Nouvelle fonction pour émettre tous les événements nécessaires pour le rafraîchissement
  const emitShopDeliveryEvent = (deliveryId, status) => {
    console.log(`Émission d'événements de rafraîchissement pour shop_delivery ${deliveryId} avec statut ${status}`);
    
    try {
      // 1. Événement spécifique pour shop_delivery
      const shopEvent = new CustomEvent('shop-delivery-status-changed', {
        detail: {
          deliveryId: deliveryId,
          status: status,
          shop: true,
          source: 'shop_delivery',
          timestamp: Date.now()
        }
      });
      window.dispatchEvent(shopEvent);
      document.dispatchEvent(shopEvent);
      
      // 2. Événement mytournee-refresh pour forcer le rafraîchissement
      const refreshEvent = new CustomEvent('mytournee-refresh', {
        detail: {
          source: 'shop_delivery',
          deliveryId: deliveryId,
          status: status,
          timestamp: Date.now()
        }
      });
      window.dispatchEvent(refreshEvent);
      document.dispatchEvent(refreshEvent);
      
      // 3. Émettre également l'événement standard delivery-updated
      const deliveryEvent = new CustomEvent('delivery-updated', {
        detail: {
          deliveryId: deliveryId,
          status: status,
          shop: true,
          source: 'shop_delivery',
          timestamp: Date.now()
        }
      });
      window.dispatchEvent(deliveryEvent);
      document.dispatchEvent(deliveryEvent);
      
      // 4. Émettre un événement de force-refresh spécifique
      window.dispatchEvent(new CustomEvent('force-app-refresh'));
      
      console.log(`Événements de rafraîchissement émis avec succès pour shop_delivery ${deliveryId}`);
    } catch (error) {
      console.error(`Erreur lors de l'émission des événements de rafraîchissement:`, error);
    }
  };
  
  return {
    // État
    isSyncing,
    hasLocalChanges,
    hasSyncErrors,
    isOnline,
    isUserOfflineMode,
    
    // Méthodes
    normalizeUpdates,
    updateShopDelivery,
    checkPendingUpdates,
    syncPendingUpdates
  };
} 